import { useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, TextField, Typography, useTheme,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PaymentIcon from "@mui/icons-material/Payment";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";

const brl = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dt = (s: string) => new Date(s).toLocaleDateString("pt-BR");
const CAT_LABEL: Record<string, string> = {
  license_charge: "Cobrança de licença", payment: "Pagamento", manual_debit: "Débito manual", manual_credit: "Crédito manual",
};

interface Entry { id: string; type: "debit" | "credit"; category: string; amount: number; competence: string; description: string; notes: string | null; receiptKey: string | null; createdAt: string }
interface InstRow { institutionId: string; name: string; cnpj: string; debits: number; credits: number; balance: number; entries: Entry[] }
interface Consolidated { totalDebits: number; totalCredits: number; totalBalance: number; institutions: InstRow[] }

export function FinanceiroPage() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [dlg, setDlg] = useState<{ kind: "payment" | "entry"; inst: InstRow } | null>(null);
  const [tab, setTab] = useState(0);

  const { data } = useQuery({
    queryKey: ["financial-consolidated", from, to],
    queryFn: async () => (await api.get<Consolidated>("/financial/consolidated", { params: { from: from || undefined, to: to || undefined } })).data,
    enabled: tab === 0,
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Financeiro — Conta-corrente</Typography>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Conta-corrente" />
        <Tab label="Relatórios" />
      </Tabs>

      {tab === 1 && <RelatoriosTab />}

      {tab === 0 && (<>
      {/* Consolidação no topo */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Card label="Total devido (débitos)" value={brl(data?.totalDebits ?? 0)} />
        <Card label="Total recebido (créditos)" value={brl(data?.totalCredits ?? 0)} />
        <Card label="Saldo devedor consolidado" value={brl(data?.totalBalance ?? 0)} color={(data?.totalBalance ?? 0) <= 0 ? "success.main" : "error.main"} />
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField label="Competência de" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Até" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Typography variant="caption" color="text.secondary">Saldo negativo = crédito a favor da instituição (pagou a mais).</Typography>
        </Stack>
      </Paper>

      {/* Agrupado por instituição */}
      {(data?.institutions ?? []).map((inst) => (
        <Accordion key={inst.institutionId} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%", pr: 2 }} justifyContent="space-between">
              <Typography sx={{ fontWeight: 600 }}>{inst.name}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">D {brl(inst.debits)} · C {brl(inst.credits)}</Typography>
                <Chip
                  label={inst.balance > 0 ? `Deve ${brl(inst.balance)}` : `Crédito ${brl(-inst.balance)}`}
                  color={inst.balance > 0 ? "error" : "success"} size="small"
                />
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button size="small" variant="contained" startIcon={<PaymentIcon />} onClick={() => setDlg({ kind: "payment", inst })}>Registrar pagamento</Button>
              <Button size="small" variant="outlined" startIcon={<PostAddIcon />} onClick={() => setDlg({ kind: "entry", inst })}>Lançar débito/crédito</Button>
            </Stack>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Competência</TableCell><TableCell>Lançamento</TableCell><TableCell>Descrição</TableCell>
                <TableCell align="right">Débito</TableCell><TableCell align="right">Crédito</TableCell><TableCell>Obs.</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {inst.entries.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>{dt(e.competence)}</TableCell>
                    <TableCell><Chip size="small" label={CAT_LABEL[e.category] ?? e.category} variant="outlined" /></TableCell>
                    <TableCell>{e.description}</TableCell>
                    <TableCell align="right">{e.type === "debit" ? brl(e.amount) : "—"}</TableCell>
                    <TableCell align="right">{e.type === "credit" ? brl(e.amount) : "—"}</TableCell>
                    <TableCell>{e.notes ?? (e.receiptKey ? "📎 comprovante" : "")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}

      {dlg && (
        <EntryDialog
          kind={dlg.kind}
          inst={dlg.inst}
          accessToken={accessToken}
          onClose={() => setDlg(null)}
          onSaved={() => { setDlg(null); qc.invalidateQueries({ queryKey: ["financial-consolidated"] }); }}
        />
      )}
      </>)}
    </Box>
  );
}

interface CashflowRow { competence: string; inflow: number; outflow: number; net: number; accumulated: number }
interface CatRow { label: string; debit: number; credit: number; net: number }
interface DebtorRow { institutionId: string; name: string; cnpj: string; debits: number; credits: number; balance: number }
interface ReportData {
  cashflowByCompetence: CashflowRow[];
  byCategory: CatRow[];
  debtors: DebtorRow[];
  totals: { inflow: number; outflow: number; net: number; receivable: number };
}
const ymLabel = (s: string) => { const [y, m] = s.split("-"); return `${m}/${y}`; };

/** Aba de relatórios financeiros: fluxo de caixa por competência, por
 *  categoria ("caixa") e devedores. */
function RelatoriosTab() {
  const theme = useTheme();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data } = useQuery({
    queryKey: ["financial-reports", from, to],
    queryFn: async () => (await api.get<ReportData>("/financial/reports", { params: { from: from || undefined, to: to || undefined } })).data,
  });
  const cf = data?.cashflowByCompetence ?? [];
  const textColor = theme.palette.text.secondary;
  const axisColor = theme.palette.divider;

  const cashflowOption = {
    backgroundColor: "transparent",
    textStyle: { color: textColor, fontFamily: theme.typography.fontFamily },
    tooltip: { trigger: "axis", formatter: (a: { axisValue: string; seriesName: string; value: number }[]) => `${a[0].axisValue}<br/>` + a.map((s) => `${s.seriesName}: <b>${brl(s.value)}</b>`).join("<br/>") },
    legend: { bottom: 0, textStyle: { color: textColor } },
    grid: { left: 8, right: 12, top: 16, bottom: 36, containLabel: true },
    xAxis: { type: "category", data: cf.map((m) => ymLabel(m.competence)), axisLabel: { color: textColor, rotate: 30 }, axisLine: { lineStyle: { color: axisColor } } },
    yAxis: { type: "value", axisLabel: { color: textColor, formatter: (v: number) => brl(v) }, splitLine: { lineStyle: { color: axisColor } } },
    series: [
      { name: "Entradas", type: "bar", data: cf.map((m) => m.inflow), itemStyle: { color: theme.palette.success.main, borderRadius: [3, 3, 0, 0] }, barMaxWidth: 26 },
      { name: "Saídas", type: "bar", data: cf.map((m) => m.outflow), itemStyle: { color: theme.palette.error.main, borderRadius: [3, 3, 0, 0] }, barMaxWidth: 26 },
      { name: "Saldo acumulado", type: "line", data: cf.map((m) => m.accumulated), smooth: true, itemStyle: { color: theme.palette.primary.main }, lineStyle: { width: 2 } },
    ],
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField label="Competência de" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Até" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Stack>
      </Paper>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Card label="Entradas (recebido)" value={brl(data?.totals.inflow ?? 0)} color="success.main" />
        <Card label="Saídas (faturado)" value={brl(data?.totals.outflow ?? 0)} color="error.main" />
        <Card label="Resultado do período" value={brl(data?.totals.net ?? 0)} color={(data?.totals.net ?? 0) >= 0 ? "success.main" : "error.main"} />
        <Card label="Saldo a receber" value={brl(data?.totals.receivable ?? 0)} color={(data?.totals.receivable ?? 0) > 0 ? "error.main" : "success.main"} />
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Fluxo de caixa por competência</Typography>
        <ReactECharts option={cashflowOption} style={{ height: 320 }} notMerge lazyUpdate />
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead><TableRow>
            <TableCell>Competência</TableCell><TableCell align="right">Entradas</TableCell>
            <TableCell align="right">Saídas</TableCell><TableCell align="right">Resultado</TableCell><TableCell align="right">Acumulado</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {cf.map((m) => (
              <TableRow key={m.competence}>
                <TableCell>{ymLabel(m.competence)}</TableCell>
                <TableCell align="right" sx={{ color: "success.main" }}>{brl(m.inflow)}</TableCell>
                <TableCell align="right" sx={{ color: "error.main" }}>{brl(m.outflow)}</TableCell>
                <TableCell align="right">{brl(m.net)}</TableCell>
                <TableCell align="right">{brl(m.accumulated)}</TableCell>
              </TableRow>
            ))}
            {cf.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>Sem movimentações no período.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Paper variant="outlined" sx={{ p: 2, flex: "1 1 360px" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Por categoria (caixa)</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Categoria</TableCell><TableCell align="right">Débito</TableCell><TableCell align="right">Crédito</TableCell><TableCell align="right">Líquido</TableCell></TableRow></TableHead>
            <TableBody>
              {(data?.byCategory ?? []).map((c) => (
                <TableRow key={c.label}>
                  <TableCell>{c.label}</TableCell>
                  <TableCell align="right">{brl(c.debit)}</TableCell>
                  <TableCell align="right">{brl(c.credit)}</TableCell>
                  <TableCell align="right">{brl(c.net)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: "1 1 360px" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Devedores (saldo a receber)</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Instituição</TableCell><TableCell align="right">Saldo</TableCell></TableRow></TableHead>
            <TableBody>
              {(data?.debtors ?? []).map((d) => (
                <TableRow key={d.institutionId}>
                  <TableCell>{d.name}</TableCell>
                  <TableCell align="right" sx={{ color: "error.main", fontWeight: 600 }}>{brl(d.balance)}</TableCell>
                </TableRow>
              ))}
              {data && data.debtors.length === 0 && <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3, color: "text.secondary" }}>Sem devedores.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
}

function Card({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: "1 1 220px", minWidth: 200 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ color: color ?? "text.primary" }}>{value}</Typography>
    </Paper>
  );
}

function EntryDialog({ kind, inst, accessToken, onClose, onSaved }: {
  kind: "payment" | "entry"; inst: InstRow; accessToken: string | null; onClose: () => void; onSaved: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [type, setType] = useState<"debit" | "credit">("credit");
  const [description, setDescription] = useState("");
  const [competence, setCompetence] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [receiptKey, setReceiptKey] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const amt = Number(amount.replace(",", "."));
      if (kind === "payment") {
        return api.post("/financial/payments", { institutionId: inst.institutionId, amount: amt, competence: competence || undefined, notes: notes || undefined, receiptKey: receiptKey || undefined });
      }
      return api.post("/financial/entries", { institutionId: inst.institutionId, type, amount: amt, description, competence: competence || undefined, notes: notes || undefined, receiptKey: receiptKey || undefined });
    },
    onSuccess: onSaved,
  });

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch("/api/files/v1/files", { method: "POST", headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}, body: fd });
      const j = await res.json();
      setReceiptKey(j.hash ?? null);
    } finally { setUploading(false); }
  }

  const valid = Number(amount.replace(",", ".")) > 0 && (kind === "payment" || description.trim().length > 0);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{kind === "payment" ? "Registrar pagamento" : "Lançar débito/crédito"} — {inst.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {kind === "entry" && (
            <TextField select label="Tipo" value={type} onChange={(e) => setType(e.target.value as "debit" | "credit")} fullWidth>
              <MenuItem value="credit">Crédito (a favor da instituição)</MenuItem>
              <MenuItem value="debit">Débito (valor devido)</MenuItem>
            </TextField>
          )}
          <TextField label="Valor (R$)" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth autoFocus inputProps={{ inputMode: "decimal" }} />
          {kind === "entry" && <TextField label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />}
          <TextField label="Competência" type="date" value={competence} onChange={(e) => setCompetence(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} helperText="Opcional — default hoje" />
          <TextField label="Observação" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
          <Button component="label" variant="outlined" disabled={uploading}>
            {uploading ? "Enviando comprovante…" : receiptKey ? "Comprovante anexado ✓" : "Anexar comprovante (imagem)"}
            <input hidden type="file" accept="image/*,.pdf" onChange={(e) => onFile(e.target.files?.[0])} />
          </Button>
          {save.isError && <Alert severity="error">Falha ao salvar o lançamento.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!valid || save.isPending} onClick={() => save.mutate()}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
}
